import { useCallback } from 'react';
import { useCreateReportTemplateMutation, useGetReportTemplateOverwriteUrlMutation, useGetReportTemplateUploadUrlMutation, } from '../api';
import { uploadToSignedUrl } from '../upload/signed-url-upload';
/**
 * SECURE Hook to upload report template files.
 *
 * This follows the simple, secure pattern:
 * 1. Request a temporary pre-signed URL from backend
 * 2. Upload file directly to object storage using that URL
 * 3. Store permanent blob URL in ReportTemplate model
 *
 * No storage credentials are exposed in the frontend!
 *
 * @returns upload and overwrite functions
 */
export const useReportTemplateFileUpload = () => {
    const [getUploadUrl] = useGetReportTemplateUploadUrlMutation();
    const [getOverwriteUrl] = useGetReportTemplateOverwriteUrlMutation();
    const [createTemplate] = useCreateReportTemplateMutation();
    /**
     * Upload a report template file securely.
     *
     * @param file The file to upload
     * @param templateData Template metadata (name, report_type, project_type, location)
     * @param onProgress Optional progress callback
     * @returns The created report template record
     */
    const upload = useCallback(async (file, templateData, onProgress) => {
        // Step 1: Get temporary SAS URL from backend
        const { uploadUrl, blobUrl } = await getUploadUrl({
            file_name: file.name,
        }).unwrap();
        // Step 2: Upload directly to storage using the temporary signed URL
        await uploadToSignedUrl(uploadUrl, file, { onProgress });
        // Step 3: Create report template record with permanent blob URL
        const template = await createTemplate({
            payload: {
                ...templateData,
                file_url: blobUrl, // Store permanent URL (no SAS token)
                file_name: file.name,
                file_extension: `.${file.name.split('.').pop() || ''}`,
            },
        }).unwrap();
        return template;
    }, [getUploadUrl, createTemplate]);
    /**
     * Overwrite an existing file in object storage.
     * This is used when editing a template to replace the file contents
     * while keeping the same blob URL.
     *
     * @param fileOrContent The new file or string content to upload
     * @param templateId The template ID to get overwrite URL for
     * @param onProgress Optional progress callback
     */
    const overwrite = useCallback(async (fileOrContent, templateId, onProgress) => {
        // Get a temporary SAS URL with write permissions for the existing blob
        const { uploadUrl } = await getOverwriteUrl({
            id: templateId,
        }).unwrap();
        // Upload the new content, overwriting the existing blob. A string is
        // wrapped in a Blob so both branches hand the uploader the same shape;
        // the type carries the content type the Azure path used to set through
        // blobHTTPHeaders.
        const body = typeof fileOrContent === 'string'
            ? new Blob([fileOrContent], { type: 'text/plain' })
            : fileOrContent;
        await uploadToSignedUrl(uploadUrl, body, { onProgress });
    }, [getOverwriteUrl]);
    return {
        upload,
        overwrite,
    };
};
